import React from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { UserAction } from "./type";
import { UserActionDialog } from "./components/UserActionDialog";
import { UserAdminInfo } from "../../../api/users";
import { useSnackbar } from "../../../hooks/useSnackbar";

/** * UserActionsMenu component provides a menu for user actions such as migrating email,
 * resending activation, and sending notifications.
 * It uses an IconButton to open a menu with options for each action.
 * When an action is selected, it updates the state to trigger the UserActionDialog.
 * The UserActionDialog component will handle rendering the appropriate form based on the selected action
 * **/

export const UserActionsMenu = ({
  user,
  refreshUsers,
}: {
  user: UserAdminInfo;
  refreshUsers: () => void;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [selectedAction, setSelectedAction] = React.useState<UserAction | null>(null);
  const { showSnackbar, SnackbarNode } = useSnackbar();

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  React.useEffect(() => {
    if (selectedAction) {
      handleCloseMenu();
    }
  }, [selectedAction]);

  return (
    <div>
      <IconButton onClick={handleOpenMenu}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
        <MenuItem onClick={() => setSelectedAction(UserAction.MIGRATE_EMAIL)}>
          Update Email
        </MenuItem>
        <MenuItem
          onClick={() => setSelectedAction(UserAction.RESEND_ACTIVATION)}
        >
          Resend Activation
        </MenuItem>
        {user.pushIds.length > 0 && (
          <MenuItem
            onClick={() => setSelectedAction(UserAction.SEND_NOTIFICATION)}
          >
            Send Push Notification
          </MenuItem>
        )}
        <MenuItem
          onClick={() => setSelectedAction(UserAction.ASSOCIATE_STRIPE_SUB)}
        >
          Associate Stripe Subscription
        </MenuItem>
      </Menu>
      <UserActionDialog
        action={selectedAction}
        user={user}
        onClose={() => setSelectedAction(null)}
        refreshUsers={refreshUsers}
        onSuccess={(message) => showSnackbar(message, "success")}
      />
      {SnackbarNode}
    </div>
  );
};
